import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { TableService } from '../services/table.service';
import { TableDataService } from '../services/tableData.service';
import { DataTable, ColumnType } from '../types/table.types';
import { TableData } from '../types/tableData.types';

export default function TableDataView() {
  const { id, dataId } = useParams<{ id: string; dataId: string }>();
  const navigate = useNavigate();

  const [table, setTable] = useState<DataTable | null>(null);
  const [data, setData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (id && dataId) {
      fetchData(id, dataId);
    }
  }, [id, dataId]);

  const fetchData = async (tableId: string, dataId: string) => {
    try {
      setLoading(true);

      const tableData = await TableService.getTableById(tableId);
      setTable(tableData);

      const recordData = await TableDataService.getTableDataById(tableId, dataId);
      setData(recordData);

      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/tables/${id}/data/${dataId}/edit`);
  };

  const handleViewHistory = () => {
    navigate(`/tables/${id}/data/${dataId}/history`);
  };

  const handleSendData = () => {
    navigate(`/transfers/create?tableId=${id}&dataId=${dataId}`);
  };

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!id || !dataId) return;

    try {
      await TableDataService.deleteTableData(id, dataId);
      navigate(`/tables/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка удаления записи');
      setConfirmDeleteOpen(false);
    }
  };

  const handleBack = () => {
    navigate(`/tables/${id}`);
  };

  const formatValue = (value: any, columnType: ColumnType): React.ReactNode => {
    if (value === null || value === undefined) {
      return <Typography color="text.secondary">Нет данных</Typography>;
    }

    switch (columnType) {
      case ColumnType.TEXT:
        return <Typography>{value}</Typography>;
      case ColumnType.NUMBER:
        return <Typography sx={{ fontWeight: 600 }}>{value}</Typography>;
      case ColumnType.DATE:
        return <Typography>{dayjs(value).format('DD.MM.YYYY')}</Typography>;
      case ColumnType.BOOLEAN:
        return <Chip
          label={value ? 'Да' : 'Нет'}
          color={value ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />;
      case ColumnType.DROPDOWN:
        return <Chip
          label={value}
          size="small"
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: '#667eea',
            fontWeight: 600
          }}
        />;
      default:
        return <Typography>{String(value)}</Typography>;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!table || !data) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Данные не найдены или у вас нет доступа к ним
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/tables')}
          sx={{ mt: 2 }}
        >
          Вернуться к списку таблиц
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Просмотр записи"
        subtitle={`Таблица: ${table.name}`}
        breadcrumbs={[
          { title: 'Таблицы', href: '/tables' },
          { title: table.name, href: `/tables/${id}` },
          { title: 'Просмотр записи' },
        ]}
      />

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Fade>
      )}

      {/* Карточка с действиями */}
      <Fade in timeout={600}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', width: 56, height: 56 }}>
                  <VisibilityIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Информация о записи
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Редактировать">
                  <IconButton
                    onClick={handleEdit}
                    sx={{
                      bgcolor: 'rgba(33, 150, 243, 0.1)',
                      color: '#2196f3',
                      '&:hover': {
                        bgcolor: 'rgba(33, 150, 243, 0.2)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="История изменений">
                  <IconButton
                    onClick={handleViewHistory}
                    sx={{
                      bgcolor: 'rgba(156, 39, 176, 0.1)',
                      color: '#9c27b0',
                      '&:hover': {
                        bgcolor: 'rgba(156, 39, 176, 0.2)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Передать">
                  <IconButton
                    onClick={handleSendData}
                    sx={{
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                      color: '#667eea',
                      '&:hover': {
                        bgcolor: 'rgba(102, 126, 234, 0.2)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Удалить">
                  <IconButton
                    onClick={handleConfirmDelete}
                    sx={{
                      bgcolor: 'rgba(244, 67, 54, 0.1)',
                      color: '#f44336',
                      '&:hover': {
                        bgcolor: 'rgba(244, 67, 54, 0.2)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Данные записи */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
              {table.columns.map((column) => (
                <Box
                  key={column.id}
                  sx={{
                    p: 2,
                    bgcolor: 'rgba(0,0,0,0.02)',
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.08)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.05)',
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                    }
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                    {column.name}
                  </Typography>
                  {formatValue(data.data[column.name], column.type)}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Метаданные */}
      <Fade in timeout={800}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Служебная информация
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(103, 58, 183, 0.05)',
                  border: '1px solid rgba(103, 58, 183, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ color: '#673ab7', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Владелец
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {data.owner?.fullName || data.owner?.username || 'Неизвестно'}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(76, 175, 80, 0.05)',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccessTimeIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Дата создания
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {dayjs(data.createdAt).format('DD.MM.YYYY HH:mm')}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(33, 150, 243, 0.05)',
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccessTimeIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Дата обновления
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {dayjs(data.updatedAt).format('DD.MM.YYYY HH:mm')}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 152, 0, 0.05)',
                  border: '1px solid rgba(255, 152, 0, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Создал
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {data.createdBy?.fullName || data.createdBy?.username || 'Неизвестно'}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(156, 39, 176, 0.05)',
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Обновил
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {data.updatedBy?.fullName || data.updatedBy?.username || 'Неизвестно'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Кнопки навигации */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Вернуться к таблице
        </Button>

        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={handleViewHistory}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#9c27b0',
            color: '#9c27b0',
            '&:hover': {
              borderColor: '#9c27b0',
              bgcolor: 'rgba(156, 39, 176, 0.08)',
            }
          }}
        >
          История изменений
        </Button>

        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            }
          }}
        >
          Редактировать
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Удаление записи"
        message="Вы уверены, что хотите удалить эту запись? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
        severity="error"
      />
    </Box>
  );
}